import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LogIn, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail, fallback = "Credenziali non valide") {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(" ");
  return fallback;
}

export default function AdminLoginPage({ onLogin }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [otp, setOtp] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data?.need_totp) {
        setChallenge(data.challenge);
        setStep(2);
      } else {
        onLogin?.(data);
        navigate("/admin/dashboard");
      }
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login/verify-totp`,
        { challenge, code: otp.trim() },
        { withCredentials: true },
      );
      onLogin?.(data);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail, "Codice non valido"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFA] flex items-center justify-center px-6" data-testid="admin-login-page">
      <Card className="w-full max-w-md rounded-2xl border-[#E5E2DB] shadow-sm">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="w-16 h-16 mx-auto bg-[#163321]/10 rounded-full flex items-center justify-center">
            {step === 1 ? (
              <img src="/logo_bianco.png" alt="Colucci Assicuratori" className="h-8 w-auto invert" />
            ) : (
              <ShieldCheck className="w-8 h-8 text-[#163321]" />
            )}
          </div>
          <CardTitle className="font-heading text-2xl text-[#0F1F14]">
            {step === 1 ? "Area Riservata" : "Verifica in due passaggi"}
          </CardTitle>
          <p className="text-sm text-[#4A5E51]">
            {step === 1
              ? "Accedi al pannello di amministrazione"
              : useBackup
                ? "Inserisci uno dei codici di recupero a 10 caratteri (formato XXXXX-XXXXX)"
                : "Inserisci il codice a 6 cifre generato dalla tua app authenticator"}
          </p>
        </CardHeader>
        <CardContent>
          {step === 1 && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-[#0F1F14]">Email</Label>
                <Input
                  id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="rounded-xl border-[#E5E2DB] focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321] h-11"
                  placeholder="admin@colucciassicuratori.com" required
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-[#0F1F14]">Password</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                    className="rounded-xl border-[#E5E2DB] focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321] h-11 pr-10"
                    required data-testid="admin-password-input"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5E51]" data-testid="toggle-password-btn">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#163321] text-white hover:bg-[#224A32] h-11 text-sm font-medium" data-testid="admin-login-btn">
                {loading ? "Accesso in corso..." : <><LogIn className="w-4 h-4 mr-2" /> Accedi</>}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyTotp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm text-[#0F1F14]">
                  {useBackup ? "Codice di recupero" : "Codice authenticator"}
                </Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode={useBackup ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  maxLength={useBackup ? 11 : 6}
                  placeholder={useBackup ? "XXXXX-XXXXX" : "123456"}
                  className="rounded-xl border-[#E5E2DB] focus:ring-2 focus:ring-[#163321]/20 focus:border-[#163321] h-14 text-center text-2xl tracking-[0.4em] font-mono"
                  required
                  autoFocus
                  data-testid="otp-input"
                />
              </div>
              {error && <p className="text-sm text-red-500" data-testid="otp-error">{error}</p>}
              <Button type="submit" disabled={loading || otp.length < 6} className="w-full rounded-full bg-[#163321] text-white hover:bg-[#224A32] h-11 text-sm font-medium" data-testid="verify-otp-btn">
                {loading ? "Verifica in corso..." : <><ShieldCheck className="w-4 h-4 mr-2" /> Conferma e accedi</>}
              </Button>
              <div className="flex items-center justify-between pt-2 border-t border-zinc-100 text-xs">
                <button
                  type="button"
                  onClick={() => { setUseBackup(v => !v); setOtp(""); setError(""); }}
                  className="text-[#163321] hover:underline flex items-center gap-1"
                  data-testid="toggle-backup-btn">
                  <KeyRound className="w-3.5 h-3.5" />
                  {useBackup ? "Usa app authenticator" : "Usa codice di recupero"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(""); setChallenge(""); setError(""); }}
                  className="text-zinc-500 hover:text-zinc-700"
                  data-testid="back-to-login-btn">
                  ← Torna al login
                </button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
